<template>
  <div class="call-panel">
    <!-- 科技感装饰元素 -->
    <div class="panel-decoration top-left"></div>
    <div class="panel-decoration top-right"></div>
    <div class="panel-decoration bottom-left"></div>
    <div class="panel-decoration bottom-right"></div>

    <!-- 主视频区域 -->
    <div class="main-video">
      <div class="video-frame">
        <!-- 只有有状态时才显示状态提示 -->
        <div class="call-status" :class="callStatus" v-if="callStatus">
          <div class="status-icon"></div>
          <span class="status-text">
            {{ callStatusText }}
          </span>
        </div>

        <!-- 原有的扫描线效果 -->
        <div class="scan-lines">
          <div class="scan-line"></div>
          <div class="scan-line"></div>
          <div class="scan-line"></div>
          <div class="scan-line"></div>
          <div class="scan-line"></div>
        </div>

        <!-- 只有选中机器人才显示user-info -->
        <div class="user-info" v-if="selectedRobot">
          <div class="user-status"></div>
          <span class="user-name">{{ user.name }}</span>
          <span class="call-time">{{ formattedCallTime }}</span>
        </div>

        <!-- 替换为动态光效粒子 -->
        <div class="voice-visualization">
          <div class="voice-particle-container">
            <div class="voice-wave"></div>
            <div class="voice-particles">
              <div class="particle" v-for="n in 30" :key="n"></div>
            </div>
            <div class="voice-ring"></div>
            <div class="voice-pulse"></div>
          </div>
        </div>

        <!-- 添加HUD元素 -->
        <div class="hud-element top-left"></div>
        <div class="hud-element top-right"></div>
        <div class="hud-element bottom-left"></div>
        <div class="hud-element bottom-right"></div>
      </div>
    </div>

    <!-- 控制按钮区域 - 只有选中机器人才显示 -->
    <div class="control-buttons" v-if="selectedRobot">
      <!-- 非连接状态显示开始通话按钮 -->
      <button class="control-btn start-call-btn"
              v-if="!isConnected"
              @click="startCall"
              :disabled="checkingMusicStatus">
        <div class="btn-glow green"></div>

        <!-- 检查音乐状态时显示加载动画 -->
        <div class="loading-dots" v-if="checkingMusicStatus">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>

        <i class="icon call-icon" v-else></i>
        <span class="btn-text">{{ checkingMusicStatus ? $translate('checkingDeviceStatus') : $translate('startIntercom') }}</span>
      </button>

      <!-- 连接状态显示结束通话按钮，加载中显示加载动画 -->
      <button class="control-btn hangup-btn"
              v-else
              @click="stopBroadcast"
              :class="{ 'is-loading': isLoading }">
        <div class="btn-glow red"></div>
        <i class="icon hangup-icon" v-if="!isLoading"></i>
        <!-- 加载状态显示三点 -->
        <div class="loading-dots" v-if="isLoading">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
        <span class="btn-text" v-if="!isLoading">{{$translate('endIntercom')}}</span>
      </button>
    </div>
  </div>
</template>
<script>
// 引入websocket.js
import { getMvpUrlMap, connectWithUrl } from '@/utils/websocket'
import api from '@/api/index'
import langMap from '@/utils/language'
import { getToken, getTenantId } from "@/libs/util";
import { mapState } from 'vuex';
import crypto from 'crypto'

export default {
  props: {
    user: {
      type: Object,
      required: true
    },
    status: {
      type: String,
      default: 'idle' // 'idle', 'connected'
    },
    selectedRobot: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      callDuration: 0, // 通话时长（秒）
      timerInterval: null,
      httpUrl: getMvpUrlMap,
      callStatus: '', // 默认为空，不显示状态
      webSocket: null, // 存储websocket实例
      isLoading: false, // 是否处于加载状态（等待socket返回）
      broadcastRtc: null,
      musicSocket: null,
      checkingMusicStatus: false, // 新增：检查音乐播放状态的加载标志
      socketHandleInit:false
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language,
    }),
    token(){
      return getToken()
    },
    isConnected() {
      // 只要不是初始状态，都显示结束通话按钮
      return this.callStatus !== '';
    },
    formattedCallTime() {
      if (!this.selectedRobot) return '00:00';

      const minutes = Math.floor(this.callDuration / 60);
      const seconds = this.callDuration % 60;

      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    callStatusText() {
      const statusMap = {
        'preparing': this.$translate('devicePreparing'),
        'waiting': this.$translate('waitingDeviceConnection'),
        'talking': this.$translate('intercomInProgress'),
        'ended': this.$translate('ended')
      };
      return statusMap[this.callStatus] || '';
    }
  },
  watch: {
    // 现有的 callStatus 监听保持不变
    callStatus(newStatus) {
      if (newStatus === 'talking') {
        this.startTimer();
        this.isLoading = false; // 结束加载状态
      } else if (newStatus === 'ended') {
        this.stopTimer();
        this.isLoading = false; // 结束加载状态
        this.callStatus = '';
      }
    },

    // 添加对 user 或 selectedRobot 的监听
    user: {
      handler(newUser, oldUser) {
        // 如果当前有通话，且切换了用户，则停止通话
        if (this.isConnected && oldUser && newUser && oldUser.serialNumber !== newUser.serialNumber) {
          this.stopBroadcast();
        }
        this.checkingMusicStatus = false;
      },
      deep: true
    },

    // 监听 selectedRobot 的变化
    selectedRobot(newVal, oldVal) {
      // 如果从选中变为未选中，且当前有通话，则停止通话
      if (oldVal && !newVal && this.isConnected) {
        this.stopBroadcast();
      }
    }
  },
  methods: {
    // WebSocket消息处理
    handleWebSocketMessage(message) {
      try {
        const data = JSON.parse(message);
        // 只处理相关设备的消息
        if (data.deviceId === this.user.serialNumber && data.channelId === this.user.serialNumber) {
          if (data.status) {
            this.callStatus = data.status;
            if(data.status == 'ended'){
              this.stopBroadcast();
            }
          }
        }
      } catch (e) {
        console.error('解析WebSocket消息失败', e);
      }
    },
    // 开始通话
    async startCall() {
      this.callStatus = 'preparing'; // 立即显示设备准备中
      this.isLoading = true
      const callStatusResult = await api.getData(
        this.httpUrl + `/api/play/broadcast/status/${this.user.serialNumber}/${this.user.serialNumber}?linkId=${this.token}`,
        this,
        true
      )
      //状态错误提醒
      if(callStatusResult.state == 0 ){
        this.$Notice.error({
          title: langMap.error[this.lang],
          desc: callStatusResult.msg,
        });
        this.callStatus = ''
        this.isLoading = false
        return
      }else if(callStatusResult.data.inUse){
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: this.$translate('robotIntercomActiveCannotCall')
        });
        return
      }
      api.getData(
        this.httpUrl + `/api/play/broadcast/${this.user.serialNumber}/${this.user.serialNumber}?broadcastMode=true&linkId=${this.token}`,
        this,
        true
      ).then(res => {
        if( res.data ){
          const streamInfo = res.data.streamInfo
          if(location.protocol.includes('https')) {
            this.startBroadcast(streamInfo.rtcs)
          }else {
            this.startBroadcast(streamInfo.rtc)
          }
        }else if(res.msg){
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: res.msg,
          });
        }
      }).catch(() => {
        // 请求失败时，回到初始状态
        this.callStatus = '';
      });
    },

    // 结束通话
    endCall() {
      if(!this.user.serialNumber){
        this.isLoading = false;
        return
      }
      api.getData(
        this.httpUrl + `/api/play/broadcast/stop/${this.user.serialNumber}/${this.user.serialNumber}?linkId=${this.token}`,
        this,
        true
      ).then(res => {
          this.stopTimer();
      }).catch(() => {
        // 请求失败时，回到初始状态
        this.callStatus = '';
        this.isLoading = false;
      });
    },
    startBroadcast(url) {
      try {
        // 检测是否支持getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: this.$translate('environmentNotSupported')
          });
          this.callStatus = '';
          return;
        }
        console.log('开始语音喊话： ')

        // 先检查音乐播放状态
        this.checkMusicPlayStatus().then(canProceed => {
          if (!canProceed) {
            this.callStatus = '';
            return;
          }

          // 音乐未播放，可以进行语音对讲
          this.callStatus = 'preparing'; // 立即显示设备准备中
          const pushKey = '3e80d1762a324d5b0ff636e0bd16f1e3'
          // 获取推流鉴权KEY
          url += '&sign=' + crypto.createHash('md5').update(pushKey, 'utf8').digest('hex')
          console.log('开始语音喊话： ' + url)
          this.broadcastRtc = new ZLMRTCClient.Endpoint({
            debug: true, // 是否打印日志
            zlmsdpUrl: url, // 流地址
            simulecast: false,
            useCamera: false,
            audioEnable: true,
            videoEnable: false,
            recvOnly: false,
          })
          // 其他已有的事件监听器保持不变
          this.broadcastRtc.on(ZLMRTCClient.Events.WEBRTC_NOT_SUPPORT, (e) => { // 获取到了本地流
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: this.$translate('webrtcNotSupported')
            })
            this.callStatus = ''
          })

          this.broadcastRtc.on(ZLMRTCClient.Events.WEBRTC_ICE_CANDIDATE_ERROR, (e) => { // ICE 协商出错
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: this.$translate('iceNegotiationError')
            })
            this.callStatus = ''
          })

          this.broadcastRtc.on(ZLMRTCClient.Events.WEBRTC_OFFER_ANWSER_EXCHANGE_FAILED, (e) => { // offer anwser 交换失败
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: this.$translate('offerAnswerExchangeFailed')
            })
            this.callStatus = ''
          })
          this.broadcastRtc.on(ZLMRTCClient.Events.WEBRTC_ON_CONNECTION_STATE_CHANGE, (e) => { // offer anwser 交换失败
            if (e === 'disconnected') {
              this.callStatus = ''
            }
          })
          this.broadcastRtc.on(ZLMRTCClient.Events.CAPTURE_STREAM_FAILED, (e) => { // offer anwser 交换失败
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: this.$translate('captureStreamFailed')
            })
            this.callStatus = ''
          })
          // 在broadcastRtc初始化后，添加音频轨道配置
          this.broadcastRtc.on(ZLMRTCClient.Events.WEBRTC_ON_LOCAL_STREAM, (stream) => {
            // 获取音频轨道
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length > 0) {
              // 应用音频约束
              audioTracks[0].applyConstraints({
                sampleRate: 8000,
                sampleSize: 16,
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
              }).catch(e => {
                console.error('无法应用音频约束:', e);
              });
            }
          });
        }).catch(error => {
          console.error('语音对讲初始化失败:', error);
          this.callStatus = '';
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: this.$translate('voiceIntercomInitFailed')
          });
        });
      } catch (error) {
        console.error('语音对讲初始化失败:', error);
        this.callStatus = '';
        this.$Notice.error({
          title: langMap.tip[this.lang],
          desc: this.$translate('voiceIntercomInitFailed')
        });
      }
    },
    stopBroadcast() {
      this.broadcastRtc && this.broadcastRtc.close()
      this.callStatus = ''
      this.isLoading = true; // 显示加载状态（三点）
      this.endCall()
    },
    // 计时器相关方法
    startTimer() {
      // 重置计时器
      this.callDuration = 0;

      // 清除可能存在的旧计时器
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      // 启动新计时器，每秒更新一次
      this.timerInterval = setInterval(() => {
        this.callDuration++;
      }, 1000);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      this.callDuration = 0; // 重置通话时长
    },

    // 添加检查音乐播放状态的方法
    checkMusicPlayStatus() {
      return new Promise((resolve, reject) => {
        const robotId = this.user?.id;
        if (!robotId) {
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: this.$translate('robotNotSelectedOrIdFailed')
          });
          resolve(false);
          return;
        }

        // 设置检查音乐状态的加载提示
        this.checkingMusicStatus = true;

        this.getMusicStatus(robotId).then(result => {
          // 结果返回后清除加载状态
          this.checkingMusicStatus = false;
          resolve(result);
        }).catch(err => {
          // 发生错误时也要清除加载状态
          this.checkingMusicStatus = false;
          resolve(false);
        });
      });
    },
    getMusicStatus(robotId){
      return new Promise((resolve,reject)=>{
        // 创建临时WebSocket连接或使用现有连接获取音乐播放状态
        const socketMessage = JSON.stringify({
          tenantId: getTenantId(),
          robotId: robotId,
          language: this.$store.state.app.language,
          type: 'refresh_volume_status'
        });
        let musicSocket = window.musicSocket || this.musicSocket;
        // 使用现有的WebSocket连接
        if (musicSocket && musicSocket.socketFlag) {
          this.socketHandleInit = false
          // 设置临时消息处理函数，接收与解析消息
          window.musicSocketMessageHandler = (data) => {
            // 使用 this.musicSocketMessageHandler 处理消息，并通过回调获取结果
            this.musicSocketMessageHandler(data, (result) => resolve(result));// 通过Promise返回结果
          };
          // 发送查询消息
          musicSocket.send(socketMessage);
        } else {
          this.socketHandleInit = true
          // 如果没有现有连接，创建临时连接
          this.createTemporaryMusicSocket(socketMessage)
            .then(result => {
              resolve(result);
            })
            .catch(error => {
              console.error('获取音乐播放状态出错:', error);
              resolve(false); // 出错时不允许通话
            });
        }
      })
    },
    // 创建临时音乐WebSocket连接
    createTemporaryMusicSocket(message) {
      return new Promise(resolve => {
        connectWithUrl('/sound', "musicSocketUrl", JSON.stringify({
          tenantId: getTenantId(),
          type: `init:${this.$store.state.user.userInfo.userId}`,
          language: this.$store.state.app.language,
        }), (data) => {
          if(this.socketHandleInit){
            this.musicSocketMessageHandler(data,(result)=>{
              resolve(result)
            })
          }else{
            window.musicSocketMessageHandler(data)
          }
        }, (socket) => {
          socket.send(message)
          this.musicSocket = socket;
        });
      });
    },
    musicSocketMessageHandler(data,callback){
      try {
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        if (parsedData.type === 'play_status') {
        // playState: 1:已停止 2:暂停中 3:做准备 4:播放中
          const robotId = this.user?.id
          if (parsedData.data.playState === 4 && robotId && parsedData.robotId == robotId) {
          // 如果正在播放音乐，询问用户是否停止
          this.confirmStopMusic( robotId, parsedData.data.filename || this.$translate('music')).then(shouldStop => {
            callback(shouldStop);
          });
        } else {
          // 没有播放音乐，可以进行语音对讲
          callback(true);
        }
      }
      } catch (error) {
        console.error('解析音乐播放状态出错:', error);
        callback(false); // 出错时不允许通话
      }
    },
    // 添加确认停止音乐播放的方法
    confirmStopMusic(robotId, songName) {
      return new Promise((resolve) => {
        this.$Modal.confirm({
          title: langMap.tip[this.lang],
          content: `${this.$translate('robotPlayingMusic')}"${songName}"${this.$translate('stopMusicAndStartIntercom')}`,
          okText: this.$translate('stopMusic'),
          cancelText: this.$translate('cancelIntercom'),
          onOk: () => {
            // 用户同意停止音乐播放
            this.stopMusicPlayback(robotId).then(success => {
              if (success) {
                // 等待一段时间确保音乐真的停止了
                setTimeout(() => {
                  // 重新检查音乐状态
                  this.getMusicStatus(robotId).then(isStoppedNow => resolve(isStoppedNow) );
                }, 1000);
              } else {
                this.$Notice.error({
                  title: langMap.tip[this.lang],
                  desc: this.$translate('stopMusicPlaybackFailed')
                });
                resolve(false);
              }
            });
          },
          onCancel: () => {
            // 用户取消，不进行语音对讲
            resolve(false);
          }
        });
      });
    },
    // 停止音乐播放的方法
    stopMusicPlayback(robotId) {
      return new Promise((resolve) => {
        // 调用停止播放命令API
        api.postData('notice-server/cmd/audio/playCommand', {
          robotId: robotId,
          data: {playCommand: 4} // 4: 停止播放
        }).then(res => {
          if (res.state === 0) {
            this.$Notice.error({
              title: langMap.tip[this.lang],
              desc: res.msg || this.$translate('stopMusicPlaybackFailed')
            });
            resolve(false);
          } else {
            this.$Notice.success({
              title: langMap.tip[this.lang],
              desc: this.$translate('musicPlaybackStopped')
            });
            resolve(true);
          }
        }).catch(error => {
          console.error('停止音乐播放出错:', error);
          this.$Notice.error({
            title: langMap.tip[this.lang],
            desc: this.$translate('stopMusicPlaybackFailed')
          });
          resolve(false);
        });
      });
    },
  },
  created() {
    console.log(this.httpUrl)
    // 初始化WebSocket连接
    connectWithUrl(`${this.httpUrl}/channel/broadcast/${this.token}`, 'VoiceIntercomURL', null, this.handleWebSocketMessage, (ws) => {
      this.webSocket = ws;
    },null,true);
  },
  beforeDestroy() {
    // 如果当前有通话，先停止通话
    this.stopBroadcast()

    // 组件销毁前清除计时器
    this.stopTimer();

    // 关闭WebSocket连接
    if (this.webSocket && this.webSocket.socketFlag) {
      this.webSocket.close();
    }
    this.musicSocket && this.musicSocket.close()
    window.musicSocketMessageHandler = null
    // 手动关闭所有可能存在的Modal弹窗
    this.$Modal.remove();
    console.log('退出语音对讲')
  }
}
</script>

<style scoped>
.call-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: rgba(10, 20, 40, 0.7);
  color: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.4),
    inset 0 0 20px rgba(0, 191, 255, 0.2);
  border: 1px solid rgba(0, 191, 255, 0.3);
  position: relative;
}

/* 面板装饰元素 */
.panel-decoration {
  position: absolute;
  width: 30px;
  height: 30px;
  z-index: 5;
}

.panel-decoration.top-left {
  top: 10px;
  left: 10px;
  border-top: 2px solid rgba(0, 191, 255, 0.7);
  border-left: 2px solid rgba(0, 191, 255, 0.7);
}

.panel-decoration.top-right {
  top: 10px;
  right: 10px;
  border-top: 2px solid rgba(0, 191, 255, 0.7);
  border-right: 2px solid rgba(0, 191, 255, 0.7);
}

.panel-decoration.bottom-left {
  bottom: 10px;
  left: 10px;
  border-bottom: 2px solid rgba(0, 191, 255, 0.7);
  border-left: 2px solid rgba(0, 191, 255, 0.7);
}

.panel-decoration.bottom-right {
  bottom: 10px;
  right: 10px;
  border-bottom: 2px solid rgba(0, 191, 255, 0.7);
  border-right: 2px solid rgba(0, 191, 255, 0.7);
}

.main-video {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, rgba(10,20,40,0.9), rgba(5,10,20,0.9));
  padding: 20px;
  overflow: hidden;
}

.video-frame {
  width: 90%;
  height: 90%;
  border: 2px solid rgba(0, 191, 255, 0.5);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  background: rgba(5, 10, 20, 0.8);
  box-shadow:
    0 0 20px rgba(0, 191, 255, 0.3),
    inset 0 0 10px rgba(0, 191, 255, 0.2);
}

/* 替换扫描线为直线缓动效果 */
.scan-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none;
}

.scan-line {
  position: absolute;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(to right,
    transparent,
    rgba(0, 191, 255, 0.5),
    rgba(0, 191, 255, 0.8),
    rgba(0, 191, 255, 0.5),
    transparent
  );
  opacity: 0.7;
}

.scan-line:nth-child(1) {
  top: 20%;
  animation: line-move 3s ease-in-out infinite;
  animation-delay: 0s;
}

.scan-line:nth-child(2) {
  top: 40%;
  animation: line-move 3s ease-in-out infinite;
  animation-delay: 0.6s;
}

.scan-line:nth-child(3) {
  top: 60%;
  animation: line-move 3s ease-in-out infinite;
  animation-delay: 1.2s;
}

.scan-line:nth-child(4) {
  top: 80%;
  animation: line-move 3s ease-in-out infinite;
  animation-delay: 1.8s;
}

.scan-line:nth-child(5) {
  top: 100%;
  animation: line-move 3s ease-in-out infinite;
  animation-delay: 2.4s;
}

@keyframes line-move {
  0% {
    transform: translateY(-20px);
    opacity: 0;
  }
  20% {
    opacity: 0.7;
  }
  80% {
    opacity: 0.7;
  }
  100% {
    transform: translateY(20px);
    opacity: 0;
  }
}

/* 删除旧的图片容器样式 */
.image-container {
  display: none;
}

.main-avatar {
  display: none;
}

/* 添加声波可视化效果 */
.voice-visualization {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.voice-particle-container {
  position: relative;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 中央波纹效果 */
.voice-wave {
  position: absolute;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(0, 191, 255, 0.8) 0%,
    rgba(0, 191, 255, 0.4) 40%,
    rgba(0, 191, 255, 0.1) 70%,
    transparent 100%
  );
  animation: pulse-wave 3s ease-in-out infinite;
  box-shadow: 0 0 30px rgba(0, 191, 255, 0.6);
  opacity: 0.8;
}

/* 外环效果 */
.voice-ring {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 2px solid rgba(0, 191, 255, 0.3);
  box-shadow:
    0 0 20px rgba(0, 191, 255, 0.2),
    inset 0 0 20px rgba(0, 191, 255, 0.2);
  animation: rotate 20s linear infinite;
}

.voice-ring::before {
  content: '';
  position: absolute;
  top: -10px;
  left: -10px;
  right: -10px;
  bottom: -10px;
  border: 1px dashed rgba(0, 191, 255, 0.2);
  border-radius: 50%;
  animation: rotate 15s linear infinite reverse;
}

.voice-ring::after {
  content: '';
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  border: 1px dotted rgba(0, 191, 255, 0.3);
  border-radius: 50%;
  animation: rotate 10s linear infinite;
}

/* 脉冲效果 */
.voice-pulse {
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid rgba(0, 191, 255, 0.5);
  animation: pulse-ring 2s ease-out infinite;
  opacity: 0;
}

/* 粒子效果 */
.voice-particles {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: rgba(0, 191, 255, 0.7);
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(0, 191, 255, 0.8);
}

/* 为每个粒子生成随机动画 */
.particle:nth-child(3n+1) {
  animation: particle-move-1 5s infinite linear;
}

.particle:nth-child(3n+2) {
  animation: particle-move-2 7s infinite linear;
  width: 3px;
  height: 3px;
}

.particle:nth-child(3n) {
  animation: particle-move-3 6s infinite linear;
  width: 5px;
  height: 5px;
}

/* 生成随机初始位置 */
.particle:nth-child(1) { top: 20%; left: 30%; animation-delay: 0s; }
.particle:nth-child(2) { top: 70%; left: 60%; animation-delay: 0.2s; }
.particle:nth-child(3) { top: 40%; left: 80%; animation-delay: 0.4s; }
.particle:nth-child(4) { top: 60%; left: 20%; animation-delay: 0.6s; }
.particle:nth-child(5) { top: 30%; left: 50%; animation-delay: 0.8s; }
.particle:nth-child(6) { top: 80%; left: 35%; animation-delay: 1s; }
.particle:nth-child(7) { top: 45%; left: 22%; animation-delay: 1.2s; }
.particle:nth-child(8) { top: 55%; left: 70%; animation-delay: 1.4s; }
.particle:nth-child(9) { top: 25%; left: 65%; animation-delay: 1.6s; }
.particle:nth-child(10) { top: 85%; left: 50%; animation-delay: 1.8s; }
.particle:nth-child(11) { top: 35%; left: 15%; animation-delay: 2s; }
.particle:nth-child(12) { top: 75%; left: 80%; animation-delay: 2.2s; }
.particle:nth-child(13) { top: 15%; left: 45%; animation-delay: 2.4s; }
.particle:nth-child(14) { top: 65%; left: 30%; animation-delay: 2.6s; }
.particle:nth-child(15) { top: 50%; left: 75%; animation-delay: 2.8s; }
.particle:nth-child(16) { top: 10%; left: 60%; animation-delay: 3s; }
.particle:nth-child(17) { top: 90%; left: 40%; animation-delay: 3.2s; }
.particle:nth-child(18) { top: 42%; left: 25%; animation-delay: 3.4s; }
.particle:nth-child(19) { top: 58%; left: 85%; animation-delay: 3.6s; }
.particle:nth-child(20) { top: 32%; left: 70%; animation-delay: 3.8s; }
.particle:nth-child(21) { top: 78%; left: 15%; animation-delay: 4s; }
.particle:nth-child(22) { top: 22%; left: 55%; animation-delay: 4.2s; }
.particle:nth-child(23) { top: 68%; left: 35%; animation-delay: 4.4s; }
.particle:nth-child(24) { top: 38%; left: 90%; animation-delay: 4.6s; }
.particle:nth-child(25) { top: 82%; left: 65%; animation-delay: 4.8s; }
.particle:nth-child(26) { top: 18%; left: 40%; animation-delay: 5s; }
.particle:nth-child(27) { top: 72%; left: 25%; animation-delay: 5.2s; }
.particle:nth-child(28) { top: 28%; left: 75%; animation-delay: 5.4s; }
.particle:nth-child(29) { top: 62%; left: 45%; animation-delay: 5.6s; }
.particle:nth-child(30) { top: 48%; left: 10%; animation-delay: 5.8s; }

/* 粒子动画 */
@keyframes particle-move-1 {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.2;
  }
  25% {
    transform: translate(30px, -20px) scale(1.2);
    opacity: 0.8;
  }
  50% {
    transform: translate(0, -40px) scale(1);
    opacity: 0.5;
  }
  75% {
    transform: translate(-30px, -20px) scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.2;
  }
}

@keyframes particle-move-2 {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.3;
  }
  33% {
    transform: translate(-25px, 25px) scale(1.3);
    opacity: 0.7;
  }
  66% {
    transform: translate(25px, 25px) scale(1.3);
    opacity: 0.7;
  }
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.3;
  }
}

@keyframes particle-move-3 {
  0% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translate(0, 40px) scale(1.4);
    opacity: 0.9;
  }
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
}

/* 波纹动画 */
@keyframes pulse-wave {
  0% {
    transform: scale(0.9);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.9;
  }
  100% {
    transform: scale(0.9);
    opacity: 0.6;
  }
}

/* 脉冲环动画 */
@keyframes pulse-ring {
  0% {
    transform: scale(0.7);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.user-info {
  position: absolute;
  top: 15px;
  left: 15px;
  display: flex;
  align-items: center;
  background: rgba(0, 20, 40, 0.7);
  padding: 8px 15px;
  border-radius: 20px;
  z-index: 10;
  border: 1px solid rgba(0, 191, 255, 0.5);
  box-shadow: 0 0 10px rgba(0, 191, 255, 0.3);
}

.user-status {
  width: 10px;
  height: 10px;
  background-color: #4caf50;
  border-radius: 50%;
  margin-right: 10px;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.7);
  animation: pulse 2s infinite;
}

.user-name {
  font-weight: 500;
  margin-right: 12px;
  text-shadow: 0 0 10px rgba(0, 191, 255, 0.7);
}

.call-time {
  background: rgba(0, 10, 20, 0.7);
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.9em;
  border: 1px solid rgba(0, 191, 255, 0.3);
}

/* HUD元素 */
.hud-element {
  position: absolute;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0, 191, 255, 0.5);
  opacity: 0.7;
}

.hud-element.top-left {
  top: 15px;
  right: 15px;
  border-right: none;
  border-bottom: none;
}

.hud-element.top-right {
  top: 15px;
  right: 15px;
  border-left: none;
  border-bottom: none;
}

.hud-element.bottom-left {
  bottom: 15px;
  left: 15px;
  border-right: none;
  border-top: none;
}

.hud-element.bottom-right {
  bottom: 15px;
  right: 15px;
  border-left: none;
  border-top: none;
}

/* 删除hud-stats相关样式 */
.hud-stats {
  display: none;
}

/* 修改控制按钮样式 */
.control-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  background: rgba(10, 20, 40, 0.9);
  flex-shrink: 0;
  position: relative;
  border-top: 1px solid rgba(0, 191, 255, 0.3);
}

.control-buttons::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 30% 50%, rgba(0, 191, 255, 0.1), transparent 70%),
    radial-gradient(circle at 70% 50%, rgba(0, 191, 255, 0.1), transparent 70%);
}

.control-btn {
  width: auto;
  height: 50px;
  border-radius: 25px;
  border: 2px solid rgba(0, 191, 255, 0.5);
  background-color: rgba(10, 30, 60, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  padding: 0 25px;
}

.control-btn:hover {
  transform: scale(1.05);
  border-color: rgba(0, 191, 255, 0.8);
}

.btn-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(0, 191, 255, 0.8) 0%, transparent 70%);
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  transition: all 0.3s;
}

.btn-glow.red {
  background: radial-gradient(circle, rgba(255, 59, 88, 0.8) 0%, transparent 70%);
}

.btn-glow.green {
  background: radial-gradient(circle, rgba(76, 175, 80, 0.8) 0%, transparent 70%);
}

.control-btn:hover .btn-glow {
  transform: translate(-50%, -50%) scale(1);
  opacity: 0.5;
}

.start-call-btn {
  background-color: rgba(10, 60, 30, 0.8);
  border-color: rgba(76, 175, 80, 0.5);
}

.start-call-btn:hover {
  border-color: rgba(76, 175, 80, 0.8);
}

.hangup-btn {
  background-color: rgba(60, 10, 20, 0.8);
  border-color: rgba(255, 59, 88, 0.5);
}

.hangup-btn:hover {
  border-color: rgba(255, 59, 88, 0.8);
}

.icon {
  width: 24px;
  height: 24px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  position: relative;
  z-index: 2;
  margin-right: 10px;
}

.start-call-btn .icon {
  filter: drop-shadow(0 0 5px rgba(76, 175, 80, 0.7));
}

.hangup-btn .icon {
  filter: drop-shadow(0 0 5px rgba(255, 59, 88, 0.7));
}

.call-icon {
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/></svg>');
}

.hangup-icon {
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white"><path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/></svg>');
}

.btn-text {
  color: white;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
  z-index: 2;
}

@keyframes pulse {
  0% {
    opacity: 0.7;
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.7);
  }
  50% {
    opacity: 1;
    box-shadow: 0 0 15px rgba(76, 175, 80, 1);
  }
  100% {
    opacity: 0.7;
    box-shadow: 0 0 5px rgba(76, 175, 80, 0.7);
  }
}

/* 添加状态样式 */
.call-status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 15;
  background: rgba(0, 20, 40, 0.7);
  border-radius: 20px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  border: 1px solid rgba(0, 191, 255, 0.5);
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.4);
}

.status-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.status-text {
  font-size: 18px;
  font-weight: 500;
  color: white;
  text-shadow: 0 0 8px rgba(0, 191, 255, 0.8);
}

/* 不同状态的颜色 */
.call-status.preparing .status-icon {
  background-color: #ffc107;
  box-shadow: 0 0 10px rgba(255, 193, 7, 0.7);
  animation: pulse 1.5s infinite;
}

.call-status.waiting .status-icon {
  background-color: #2196f3;
  box-shadow: 0 0 10px rgba(33, 150, 243, 0.7);
  animation: pulse 1s infinite;
}

.call-status.talking .status-icon {
  background-color: #4caf50;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.7);
  animation: pulse 2s infinite;
}

.call-status.ended .status-icon {
  background-color: #f44336;
  box-shadow: 0 0 10px rgba(244, 67, 54, 0.7);
}

/* 加载中的样式 */
.control-btn.is-loading {
  cursor: wait;
}

.loading-dots {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dot {
  width: 6px;
  height: 6px;
  background: white;
  border-radius: 50%;
  margin: 0 3px;
  animation: dot-pulse 1.5s infinite ease-in-out;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-pulse {
  0% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
}

.control-btn:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.control-btn .loading-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.control-btn .loading-dots .dot {
  width: 6px;
  height: 6px;
  background-color: white;
  border-radius: 50%;
  animation: dot-pulse 1.5s infinite ease-in-out;
}

.control-btn .loading-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.control-btn .loading-dots .dot:nth-child(2) {
  animation-delay: 0.5s;
}

.control-btn .loading-dots .dot:nth-child(3) {
  animation-delay: 1s;
}

@keyframes dot-pulse {
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}
</style>
